/**
 * EV Charging Overlay - HAL 3000
 * Popup affiché quand la MG4 est en charge
 * Affiche: puissance, temps restant, SoC, arrêt d'urgence
 */
import { useState, useEffect, useCallback } from 'react';
import { Zap, X, AlertTriangle, Battery, Clock, Gauge } from 'lucide-react';
import { api } from '@/App';

const EVChargingOverlay = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [data, setData] = useState(null);
  const [charger, setCharger] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [vehicleRes, chargerRes] = await Promise.all([
        api.get('/integrations/vehicles/status').catch(() => ({ data: null })),
        api.get('/integrations/vehicles/charger/status').catch(() => ({ data: null })),
      ]);

      const mg4 = vehicleRes?.data?.mg4;
      if (mg4?.connected && mg4?.data?.charging) {
        setData(mg4.data);
        if (!dismissed) setVisible(true);
      } else {
        setVisible(false);
        setDismissed(false); // Reset dismiss when charging stops
      }

      if (chargerRes?.data?.success) {
        setCharger(chargerRes.data.charger);
      }
    } catch (e) {
      console.error('EV overlay error:', e);
    }
  }, [dismissed]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 120000); // Check every 2min (uses cache, no re-login)
    return () => clearInterval(interval);
  }, [loadData]);

  const formatTime = (minutes) => {
    if (!minutes || minutes >= 1000) return '--';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${m}min`;
  };

  const handleEmergencyStop = async () => {
    if (!window.confirm('ARRÊTER LA CHARGE ?')) return;
    try {
      // Try to stop charger via Tuya
      if (charger?.device_id) {
        await api.post(`/integrations/tuya/devices/${charger.device_id}/command`, {
          code: 'switch',
          value: false
        });
      }
      setVisible(false);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (!visible || !data) return null;

  const batteryPct = data.battery_pct;
  const rangeKm = data.range_km;
  const chargingMin = data.charging_remaining_min;
  const powerKw = charger?.power_kw || data.realtime_power_kw;
  const targetSoc = data.target_soc;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      data-testid="ev-charging-overlay"
    >
      <div className="w-[520px] rounded-2xl border-2 border-green-500/60 bg-gradient-to-br from-gray-900 to-green-950/30 p-6 relative">
        {/* Close button */}
        <button
          onClick={() => { setDismissed(true); setVisible(false); }}
          className="absolute top-3 right-3 p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-all"
          data-testid="ev-overlay-close"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-full bg-green-500/20 border border-green-500/40">
            <Zap className="w-8 h-8 text-green-400 animate-pulse" />
          </div>
          <div>
            <div className="text-xl font-mono font-bold text-green-400">MG4 EN CHARGE</div>
            <div className="text-sm font-mono text-gray-400">Chargeur 7,4 kW</div>
          </div>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Battery */}
          <div className="p-4 rounded-xl bg-black/40 border border-green-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Battery className="w-5 h-5 text-green-400" />
              <span className="text-sm font-mono text-gray-400">BATTERIE</span>
            </div>
            <div className="text-4xl font-bold font-mono text-green-400">
              {batteryPct?.toFixed(0) || '--'}%
            </div>
            {/* Progress bar */}
            <div className="mt-2 h-3 bg-gray-800 rounded-full overflow-hidden relative">
              <div 
                className="h-full rounded-full transition-all duration-1000"
                style={{ 
                  width: `${batteryPct || 0}%`, 
                  background: 'linear-gradient(90deg, #22c55e, #4ade80)',
                  boxShadow: '0 0 10px #22c55e80'
                }} 
              />
              {targetSoc && (
                <div 
                  className="absolute top-0 bottom-0 w-0.5 bg-yellow-400"
                  style={{ left: `${targetSoc}%` }}
                  title={`Cible: ${targetSoc}%`}
                />
              )}
            </div>
            <div className="flex justify-between mt-1 text-xs font-mono text-gray-500">
              <span>0%</span>
              {targetSoc && <span className="text-yellow-400">Cible: {targetSoc}%</span>}
              <span>100%</span>
            </div>
          </div>

          {/* Power & Time */}
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-black/40 border border-cyan-500/20">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-mono text-gray-400">PUISSANCE</span>
              </div>
              <div className="text-3xl font-bold font-mono text-cyan-400">
                {powerKw ? `${powerKw}` : '--'} <span className="text-lg">kW</span>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-black/40 border border-yellow-500/20">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-mono text-gray-400">TEMPS RESTANT</span>
              </div>
              <div className="text-3xl font-bold font-mono text-yellow-400">
                {formatTime(chargingMin)}
              </div>
            </div>
          </div>
        </div>

        {/* Range */}
        <div className="p-3 rounded-xl bg-black/40 border border-gray-700 flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-blue-400" />
            <span className="text-sm font-mono text-gray-400">AUTONOMIE ESTIMÉE</span>
          </div>
          <div className="text-2xl font-bold font-mono text-blue-400">
            {rangeKm || '--'} <span className="text-sm">km</span>
          </div>
        </div>

        {/* Emergency Stop */}
        <button
          onClick={handleEmergencyStop}
          className="w-full py-4 rounded-xl bg-red-900/40 border-2 border-red-500/60 text-red-400 font-mono font-bold text-lg tracking-wider hover:bg-red-900/60 hover:border-red-400 transition-all active:scale-[0.98]"
          data-testid="ev-emergency-stop"
        >
          <div className="flex items-center justify-center gap-3">
            <AlertTriangle className="w-6 h-6" />
            ARRÊT D'URGENCE
          </div>
        </button>
      </div>
    </div>
  );
};

export default EVChargingOverlay;
